// LegacyProjectManager.jsx
import React, { useState, useRef, useEffect } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ShipDialog from "../ShipDialog";
import { getRequest, postRequest } from "../../services/apiService";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { InputText } from 'primereact/inputtext';

const ActionCell = ({ rowData, onEdit, onDelete, onShip }) => {
  const op = useRef(null);
  const menuItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        op.current.hide();
        onEdit(rowData);
      }
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: () => {
        op.current.hide();
        onDelete(rowData);
      }
    },
    {
      label: "Add Ship",
      icon: "pi pi-compass",
      command: () => {
        op.current.hide();
        onShip(rowData);
      }
    }
  ];

  return (
    <>
      <Button
        icon="pi pi-ellipsis-h"
        className="p-button-rounded p-button-text"
        onClick={(e) => op.current.toggle(e)}
      />
      <OverlayPanel ref={op} className="p-0 shadow-lg">
        <Menu model={menuItems} className="border-none" />
      </OverlayPanel>
    </>
  );
};

const normalizeResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data.results && Array.isArray(data.results.data)) return data.results.data;
  if (data.data && Array.isArray(data.data)) return data.data;
  return [];
};

export default function LegacyProjectManager() {
  const [projects, setProjects] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [shipDialogVisible, setShipDialogVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
  });
  const toast = useRef(null);

  const [newProject, setNewProject] = useState({
    id: null,
    name: "",
    code: "",
    status: "Not Started",
    class: null,
  });

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  const [deleteFormData, setDeleteFormData] = useState({ confirmation: "" });

  const [newShip, setNewShip] = useState({
    name: "",
    startDate: "",
    endDate: "",
    budget: "",
    shipyard: null,
    parent: null
  });

  const [shipyardOptions, setShipyardOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [showNewClassInput, setShowNewClassInput] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [newClassCode, setNewClassCode] = useState("");
  const [searchClassTerm, setSearchClassTerm] = useState("");


  const statusMap = {
    "Not Started": 1,
    "In Progress": 2,
    "Completed": 3,
    "On Hold": 4,
    "Deleted": 3
  };

  const columns = [
    {
      field: "name",
      header: "Project Name",
      sortable: true
    },
    {
      field: "code",
      header: "Project Code",
      sortable: true
    },
    {
      field: "class_name",
      header: "Class",
      sortable: true,
      body: (rowData) => rowData.class_name
    },
    {
      field: "actions",
      header: "Actions",
      body: (rowData) => (
        <ActionCell
          rowData={rowData}
          onEdit={openEditDialog}
          onDelete={openDeleteDialog}
          onShip={openShipDialog}
        />
      ),
      sortable: false,
      notFilterable: true
    }
  ];

  const projectFormFields = [
    {
      name: 'name',
      label: 'Project Name',
      type: 'text',
      required: true,
      placeholder: 'Enter project name'
    },
    {
      name: 'code',
      label: 'Project Code',
      type: 'text',
      required: true,
      placeholder: 'Enter project code'
    },
    {
      name: 'class',
      label: 'Class',
      type: 'select',
      options: classOptions,
      required: true,
      placeholder: 'Select a class',
      filter: true,
      onFilter: (e) => setSearchClassTerm(e.filter),
      onChange: (e, field, formData, setFormData) => {
        setFormData(prev => ({ ...prev, [field.name]: e.value }));
        if (e.value === null || e.value === 'addNewClass') {
          setShowNewClassInput(true);
        } else {
          setShowNewClassInput(false);
          setNewClassName("");
          setNewClassCode("");
        }
      },
      itemTemplate: (option) => {
        if (option.value === 'addNewClass') {
          return (
            <div className="flex items-center justify-between p-2">
              <span className="text-blue-500 font-semibold">Add New Class</span>
              <Button
                icon="pi pi-plus"
                className="p-button-sm p-button-text p-button-rounded"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowNewClassInput(true);
                }}
              />
            </div>
          );
        }
        return (
          <div className="p-2">
            {option.label}
          </div>
        );
      },
      selectedItemTemplate: (option, props) => {
        if (option && option.label && option.value !== 'addNewClass') {
          return option.label;
        }
        return props.placeholder;
      }
    }
  ];

  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const fetchProjects = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const data = await getRequest(`/master/projects/?page=${page}&legacy=True`);
      const projArray = normalizeResponse(data);

      const projectsWithClassNames = projArray.map(project => {
        const classId = project.class_name ? String(project.class_name.id) : null;
        const classNameLabel = project.class_name ? project.class_name.name : 'N/A';

        return {
          ...project,
          class: classId,
          class_name: classNameLabel
        };
      });

      setProjects(projectsWithClassNames);
      setTotalRecords(data.count || 0);

      setLazyState(prev => ({
        ...prev,
        first: (page - 1) * pageSize,
        page
      }));
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to fetch projects",
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchShipyards = async () => {
    try {
      const data = await getRequest("/master/shipyards/");
      const options = normalizeResponse(data);
      const formatted = options.map((opt) => ({
        label: opt.name,
        value: String(opt.id)
      }));
      setShipyardOptions(formatted);
    } catch (error) {
      console.error("Error fetching shipyards:", error);
    }
  };

  const fetchClasses = async () => {
    try {
      const data = await getRequest("/master/classes/");
      const options = normalizeResponse(data);
      let formatted = options.map((opt) => ({
        label: opt.name,
        value: String(opt.id)
      }));

      const classFound = formatted.some(option =>
        option.label.toLowerCase() === searchClassTerm.toLowerCase()
      );

      if (searchClassTerm && !classFound && searchClassTerm.trim() !== "") {
        formatted = [...formatted, { label: `+ Add "${searchClassTerm}" as class`, value: 'addNewClass' }];
      }

      setClassOptions(formatted);
      return formatted;
    } catch (error) {
      console.error("Error fetching classes:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchClasses();
  }, [searchClassTerm]);


  const onPageChange = (event) => {
    const newPage = (event.first / event.rows) + 1;
    setLazyState(event);
    fetchProjects(newPage, event.rows);
  };

  useEffect(() => {
    fetchClasses();
    fetchShipyards();
  }, []);

  useEffect(() => {
    if (classOptions.length > 0 || !search) {
        fetchProjects(lazyState.page, lazyState.rows);
    }
  }, [search, lazyState.page, classOptions]);


  const handleTableSearch = (value) => {
    setSearch(value);
  };

  const openAddDialog = () => {
    setNewProject({
      id: null,
      name: "",
      code: "",
      status: "Not Started",
      class: null,
    });
    setIsEditing(false);
    setDialogVisible(true);
    setShowNewClassInput(false);
    setNewClassName("");
    setNewClassCode("");
    setSearchClassTerm("");
  };

  const openEditDialog = async (project) => {
    const reverseStatus = (dbVal) => {
      const entries = Object.entries(statusMap);
      const found = entries.find(([key, val]) => val === dbVal);
      return found ? found[0] : "Not Started";
    };

    setLoading(true);
    try {
      await fetchClasses();
      
      setNewProject({
        id: project.id,
        name: project.name || "",
        code: project.code || "",
        status: reverseStatus(project.status_db),
        class: project.class
      });
      
      setIsEditing(true);
      setDialogVisible(true);
      setShowNewClassInput(false);
      setNewClassName("");
      setNewClassCode("");
      setSearchClassTerm("");
    } finally {
      setLoading(false);
    }
  };

  const openShipDialog = (project) => {
    setSelectedProject(project);
    setNewShip(prev => ({
      ...prev,
      parent: project.id
    }));
    setShipDialogVisible(true);
  };

  const openDeleteDialog = (project) => {
    setSelectedProject(project);
    setDeleteFormData({ confirmation: "" });
    setDeleteDialogVisible(true);
  };

  const showNotification = (message, type) => {
    toast.current.show({
      severity: type,
      summary: type === 'success' ? 'Success' : type === 'warning' ? 'Warning' : 'Error',
      detail: message,
      life: 3000
    });
  };

  const handleAddNewClass = async () => {
    if (!newClassName || !newClassCode) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Class name and code are required",
        life: 3000
      });
      return;
    }

    try {
      const response = await postRequest("/master/classes/", {
        name: newClassName,
        code: newClassCode,
      });

      const newClassId = String(response.id);

      await fetchClasses();

      setNewProject(prev => ({
        ...prev,
        class: newClassId
      }));

      showNotification('Class added successfully', 'success');
      setShowNewClassInput(false);
      setNewClassName("");
      setNewClassCode("");
      setSearchClassTerm("");

    } catch (error) {
      console.error("Error adding new class_name:", error);
      showNotification('Failed to add new class', 'error');
    }
  };

  const saveProject = async () => {
    if (!newProject.name || !newProject.code) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Project name and code are required",
        life: 3000
      });
      return;
    }
    if (!newProject.class) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Project class is required",
        life: 3000
      });
      return;
    }

    try {
      const payload = {
        name: newProject.name,
        code: newProject.code,
        status_db: statusMap[newProject.status] || 1,
        class_name: newProject.class,
        legacy: true,
      };

      if (isEditing) {
        payload.id = newProject.id;
      }

      await postRequest("/master/projects/", payload);

      showNotification(
        isEditing
          ? 'Project updated successfully'
          : 'Project added successfully',
        'success'
      );

      await fetchProjects(lazyState.page, lazyState.rows);
      setDialogVisible(false);
    } catch (error) {
      console.error("Error saving project:", error);
      showNotification('Failed to save project', 'error');
    }
  };

  const saveShipProject = async () => {
    if (!newShip.name) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Ship name is required",
        life: 3000,
      });
      return;
    }
    if (!newShip.shipyard) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a shipyard",
        life: 3000,
      });
      return;
    }
    try {
      const payload = {
        name: newShip.name,
        parent: newShip.parent,
        budget: Number(newShip.budget),
        start_date: newShip.startDate,
        end_date: newShip.endDate,
        yard: newShip.shipyard,
        status_db: 1,
      };
      await postRequest("/master/projects/", payload);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Ship project added successfully",
        life: 3000,
      });
      await fetchProjects(lazyState.page, lazyState.rows);
      setShipDialogVisible(false);
    } catch (error) {
      console.error("Error saving ship project:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to add ship project",
        life: 3000,
      });
    }
  };

  const confirmDelete = async () => {
    if (deleteFormData.confirmation !== "DELETE") {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000
      });
      return;
    }
    if (!selectedProject) return;
    try {
      await postRequest("/master/projects/", {
        id: selectedProject.id,
        status_db: 3
      });

      showNotification('Project deleted successfully', 'warning');
      await fetchProjects(lazyState.page, lazyState.rows);
      setDeleteDialogVisible(false);
      setSelectedProject(null);
      setDeleteFormData({ confirmation: "" });
    } catch (error) {
      console.error("Error deleting project:", error);
      showNotification('Failed to delete project', 'error');
    }
  };

  const fetchShipsForProject = async (projectId) => {
    try {
      const response = await getRequest(`/master/projects/?parent=${projectId}`);
      return normalizeResponse(response);
    } catch (error) {
      console.error("Error fetching ships:", error);
      return [];
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <Toast ref={toast} position="top-right" />

      <ReusableTable
        title="Projects"
        data={projects}
        columnFields={columns}
        rows={lazyState.rows}
        colorScheme={{
          headerBgColor: '#1e3a8a',
          headerTextColor: '#ffffff',
          tableBgColor: '#ffffff',
          tableTextColor: '#334155',
          buttonBgColor: '#3b82f6',
          buttonTextColor: '#ffffff',
          deleteButtonColor: '#ef4444',
          addButtonColor: '#10b981',
          uploadButtonColor: '#f59e0b',
          exportButtonColor: '#6366f1'
        }}
        expandableRows
        expandedRows={expandedRows}
        onRowExpand={async (event) => {
          const projectId = event.data.id;
          const ships = await fetchShipsForProject(projectId);
          setExpandedRows({
            ...expandedRows,
            [projectId]: ships
          });
        }}
        setAddNewDialogVisible={openAddDialog}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        first={lazyState.first}
        loading={loading}
        searchValue={search}
        onSearchChange={handleTableSearch}
        expandedRowTemplate={(rowData) => {
          const ships = expandedRows[rowData.id] || [];
          return (
            <div className="p-4 bg-gray-50 border-t border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-semibold">Ships</h3>
                <Button
                  icon="pi pi-plus"
                  label="Add Ship"
                  className="p-button-sm p-button-success"
                  onClick={() => openShipDialog(rowData)}
                />
              </div>
              {ships.length > 0 ? (
                <div className="space-y-2">
                  {ships.map((ship) => (
                    <div key={ship.id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm border border-gray-200">
                      <div className="flex items-center space-x-4">
                        <i className="pi pi-anchor text-blue-500" />
                        <div>
                          <div className="font-medium">{ship.name}</div>
                          <div className="text-sm text-gray-500">Yard: {ship.yard}</div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-500">
                        <div>Start: {formatDate(ship.start_date)}</div>
                        <div>End: {formatDate(ship.end_date)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <i className="pi pi-anchor text-4xl mb-2" />
                  <p>No ships added yet</p>
                </div>
              )}
            </div>
          );
        }}
      />

      <Dialog visible={dialogVisible}
        header={isEditing ? "Edit Project" : "Add New Project"} modal
        className="max-w-4xl w-full"
        onHide={() => setDialogVisible(false)}
        headerClassName="bg-indigo-700 text-white px-6 py-4 "
      >
        <ReusableForm
          formData={newProject}
          setFormData={setNewProject}
          fields={projectFormFields}
          onSubmit={saveProject}
          onHide={() => setDialogVisible(false)}
          loading={loading}
          submitLabel={isEditing ? "Update Project" : "Create Project"}
        />

        {showNewClassInput && (
          <div className="p-4 border-t border-gray-200 mt-4 bg-gray-50 rounded-b-xl">
            <h4 className="text-md font-semibold mb-3">Add New Class</h4>
            <div className="flex flex-col gap-3">
              <div>
                <label htmlFor="newClassName" className="block text-sm font-medium text-gray-700 mb-1">Class Name</label>
                <InputText
                  id="newClassName"
                  value={newClassName}
                  onChange={(e) => setNewClassName(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Enter new class name"
                />
              </div>
              <div>
                <label htmlFor="newClassCode" className="block text-sm font-medium text-gray-700 mb-1">Class Code</label>
                <InputText
                  id="newClassCode"
                  value={newClassCode}
                  onChange={(e) => setNewClassCode(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="Enter new class code"
                />
              </div>
              <Button
                label="Save New Class"
                icon="pi pi-save"
                onClick={handleAddNewClass}
                className="p-button-success mt-2"
                loading={loading}
              />
            </div>
          </div>
        )}
      </Dialog>

      {shipDialogVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-transparent w-full max-w-3xl animate-fade-in">
            <ShipDialog
              visible={shipDialogVisible}
              onHide={() => setShipDialogVisible(false)}
              projectData={selectedProject}
              fetchProjects={fetchProjects}
              toast={toast}
              isModalOverlay={true}
            />
          </div>
        </div>
      )}

      <ReusableDeleteDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onDelete={confirmDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Project"
        description={`Are you sure you want to delete project \"${selectedProject?.name}\"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />
    </div>
  );
}